import React from "react";
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CProgress,
  CProgressBar,
} from "@coreui/react";
import { CChartDoughnut, CChartBar } from "@coreui/react-chartjs";
import { buildPieItems, fmtInt } from "../../utils/chartHelpers";
import PinButton from "../UI/Pins/PinButton";

const TOOL_TITLES = {
  get_revenue_overview: "Сводка по выручке",
  get_category_sales: "Продажи по категориям",
  get_payment_types: "Типы оплат",
  get_waiter_stats: "Официанты",
  get_dish_stats: "Статистика блюд",
  get_writeoffs: "Списания",
  get_dangerous_events: "Опасные операции",
  get_cash_shifts: "Кассовые смены",
  get_revenue_trend: "Тренд выручки",
  get_stock_balances: "Остатки на складе",
  get_sales_forecast: "Прогноз продаж",
  get_menu_matrix: "Меню-инжиниринг",
  get_daypart_analysis: "Загрузка по часам",
  get_discount_effectiveness: "Эффективность скидок",
  get_promotions_report: "Отчёт по акциям",
  get_stock_depletion_forecast: "Прогноз списания склада",
  get_stock_anomalies: "Аномалии остатков",
  get_purchase_risk_list: "Риски закупки",
  get_inventory_audit: "Аудит инвентаризаций",
};

const PALETTE = ["#41B883", "#E46651", "#00D8FF", "#DD1B16", "#23e033", "#32aebe", "#d15bd1", "#f6b93b"];

function PeriodCaption({ result }) {
  if (!result?.date_from || !result?.date_to) return null;
  return (
    <div className="small text-body-secondary mb-2">
      {result.date_from} — {result.date_to}
      {result.date_clamped && " (сегодня исключено — день ещё не завершён)"}
    </div>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="border rounded p-2 text-center" style={{ minWidth: 110, flex: "1 1 110px" }}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-semibold fs-6">{value}</div>
    </div>
  );
}

function RevenueOverviewView({ result }) {
  if (result.days === 0) return <div className="text-body-secondary small">Нет данных за период.</div>;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-wrap gap-2">
        <StatTile label="Выручка" value={`${fmtInt(result.total_revenue)} ₽`} />
        <StatTile label="Гостей" value={fmtInt(result.total_guests)} />
        <StatTile label="Чеков" value={fmtInt(result.total_orders)} />
        <StatTile label="Средний чек" value={result.average_check != null ? `${fmtInt(result.average_check)} ₽` : "—"} />
        <StatTile label="Себестоимость" value={`${result.average_cost_percent ?? "—"}%`} />
        <StatTile label="Возвраты" value={`${fmtInt(result.total_returns)} ₽`} />
      </div>
    </>
  );
}

/** Doughnut + table for any {name, sum, share_percent}[] list (categories, payment types, waiters, writeoff reasons/employees). */
function ShareListView({ result, items, sumLabel = "Сумма" }) {
  if (!items || items.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  const pie = buildPieItems(items.map((i) => i.name), items.map((i) => i.sum), PALETTE);
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-column flex-md-row gap-3 align-items-start">
        <div style={{ width: 160, flex: "0 0 auto" }}>
          <CChartDoughnut
            data={{
              labels: pie.map((x) => x.label),
              datasets: [{ backgroundColor: pie.map((x) => x.color), data: pie.map((x) => x.value) }],
            }}
            options={{ plugins: { legend: { display: false } } }}
          />
        </div>
        <CTable small hover responsive className="mb-0 flex-grow-1">
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell />
              <CTableHeaderCell>Название</CTableHeaderCell>
              <CTableHeaderCell>{sumLabel}</CTableHeaderCell>
              <CTableHeaderCell>Доля</CTableHeaderCell>
            </CTableRow>
          </CTableHead>
          <CTableBody>
            {pie.map((it) => (
              <CTableRow key={it.label}>
                <CTableDataCell>
                  <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: it.color }} />
                </CTableDataCell>
                <CTableDataCell>{it.label}</CTableDataCell>
                <CTableDataCell>{fmtInt(it.value)} ₽</CTableDataCell>
                <CTableDataCell>
                  {items.find((i) => i.name === it.label)?.share_percent ?? 0}%
                </CTableDataCell>
              </CTableRow>
            ))}
          </CTableBody>
        </CTable>
      </div>
    </>
  );
}

function DishTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  const shown = rows.slice(0, 8);
  return (
    <div className="mb-2">
      <div className="small fw-semibold text-body-secondary mb-1">{title}</div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Блюдо</CTableHeaderCell>
            <CTableHeaderCell>Категория</CTableHeaderCell>
            <CTableHeaderCell>Кол-во</CTableHeaderCell>
            <CTableHeaderCell>Выручка</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {shown.map((d, i) => (
            <CTableRow key={`${d.id ?? d.name}-${i}`}>
              <CTableDataCell>{d.name}</CTableDataCell>
              <CTableDataCell className="text-body-secondary">{d.category}</CTableDataCell>
              <CTableDataCell>{fmtInt(d.qty)}</CTableDataCell>
              <CTableDataCell>{fmtInt(d.revenue)} ₽</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
      {rows.length > shown.length && (
        <div className="small text-body-secondary mt-1">и ещё {rows.length - shown.length}…</div>
      )}
    </div>
  );
}

function DishStatsView({ result }) {
  return (
    <>
      <PeriodCaption result={result} />
      <div className="small text-body-secondary mb-2">
        В активном меню {result.active_menu_count} позиций, продавалось {result.sold_count}.
      </div>
      <DishTable title="Топ продаж" rows={result.top_sellers} />
      <DishTable title="Наименее популярные" rows={result.least_popular} />
    </>
  );
}

function BarList({ items, color = "info" }) {
  if (!items || items.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  const max = Math.max(...items.map((i) => i.sum ?? i.count ?? 0), 1);
  return (
    <div className="d-flex flex-column gap-2">
      {items.map((it, i) => {
        const value = it.sum ?? it.count ?? 0;
        return (
          <div key={`${it.name}-${i}`}>
            <div className="d-flex justify-content-between small">
              <span>{it.name}</span>
              <span className="text-body-secondary">
                {it.sum != null ? `${fmtInt(it.sum)} ₽` : fmtInt(it.count)}
                {it.share_percent != null && ` · ${it.share_percent}%`}
              </span>
            </div>
            <CProgress thin color={color}>
              <CProgressBar value={(value / max) * 100} />
            </CProgress>
          </div>
        );
      })}
    </div>
  );
}

function WriteoffsView({ result }) {
  return (
    <>
      <PeriodCaption result={result} />
      {result.by_reason?.length > 0 && (
        <div className="mb-3">
          <div className="small fw-semibold text-body-secondary mb-1">По причине</div>
          <BarList items={result.by_reason} color="warning" />
        </div>
      )}
      {result.by_employee?.length > 0 && (
        <div>
          <div className="small fw-semibold text-body-secondary mb-1">По сотруднику</div>
          <BarList items={result.by_employee} color="warning" />
        </div>
      )}
    </>
  );
}

function DangerousEventsView({ result }) {
  return (
    <>
      <PeriodCaption result={result} />
      <div className="mb-2">
        <CBadge color={result.total > 0 ? "danger" : "success"}>Всего: {result.total}</CBadge>
      </div>
      {result.by_type?.length > 0 && (
        <div className="mb-3">
          <div className="small fw-semibold text-body-secondary mb-1">По типу</div>
          <BarList items={result.by_type.map((t) => ({ name: t.label, count: t.count }))} color="danger" />
        </div>
      )}
      {result.top_employees?.length > 0 && (
        <div>
          <div className="small fw-semibold text-body-secondary mb-1">По сотруднику</div>
          <BarList items={result.top_employees.map((e) => ({ name: e.name, count: e.count }))} color="danger" />
        </div>
      )}
    </>
  );
}

const STATUS_COLORS = { CLOSED: "secondary", OPEN: "success", HASWARNINGS: "warning" };

function CashShiftsView({ result }) {
  if (!result.shifts || result.shifts.length === 0) return <div className="text-body-secondary small">Нет смен за период.</div>;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-wrap gap-1 mb-2">
        <CBadge color="secondary">Всего: {result.total}</CBadge>
        {Object.entries(result.by_status || {}).map(([status, count]) => (
          <CBadge key={status} color={STATUS_COLORS[status] || "info"}>
            {status}: {count}
          </CBadge>
        ))}
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>№</CTableHeaderCell>
            <CTableHeaderCell>Открыта</CTableHeaderCell>
            <CTableHeaderCell>Закрыта</CTableHeaderCell>
            <CTableHeaderCell>Статус</CTableHeaderCell>
            <CTableHeaderCell>Ответственный</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {result.shifts.map((s, i) => (
            <CTableRow key={i}>
              <CTableDataCell>{s.session_number ?? "—"}</CTableDataCell>
              <CTableDataCell className="text-nowrap">{s.open_date}</CTableDataCell>
              <CTableDataCell className="text-nowrap">{s.close_date || "—"}</CTableDataCell>
              <CTableDataCell>
                <CBadge color={STATUS_COLORS[s.status] || "info"}>{s.status}</CBadge>
              </CTableDataCell>
              <CTableDataCell>{s.responsible || s.manager || "—"}</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </>
  );
}

function RevenueTrendView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const pct = result.vs_prior_days_pct;
  const up = pct != null && pct > 0;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-wrap gap-2 align-items-center">
        <StatTile label="Этот период, в день" value={`${fmtInt(result.avg_daily_revenue_this_period)} ₽`} />
        <StatTile label="До этого, в день" value={`${fmtInt(result.avg_daily_revenue_prior_days)} ₽`} />
        {pct != null && (
          <span className={`fw-semibold ${up ? "text-success" : "text-danger"}`}>
            {up ? "↑" : "↓"} {Math.abs(pct)}%
          </span>
        )}
      </div>
    </>
  );
}

export function StockBalancesView({ result }) {
  if (!result.items || result.items.length === 0) {
    return <div className="text-body-secondary small">Остатков не найдено.</div>;
  }
  return (
    <>
      <div className="small text-body-secondary mb-2">
        Остатки на {new Date(result.balance_at).toLocaleString("ru-RU")}
        {result.cached && " (из кэша, обновляется не чаще раза в 5 минут)"}
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Товар</CTableHeaderCell>
            <CTableHeaderCell>Склад</CTableHeaderCell>
            <CTableHeaderCell>Кол-во</CTableHeaderCell>
            <CTableHeaderCell>Сумма</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {result.items.map((it, i) => (
            <CTableRow key={i}>
              <CTableDataCell>{it.product}</CTableDataCell>
              <CTableDataCell className="text-body-secondary">{it.store}</CTableDataCell>
              <CTableDataCell className={it.quantity < 0 ? "text-danger fw-semibold" : undefined}>
                {it.quantity} {it.unit}
              </CTableDataCell>
              <CTableDataCell>{it.cost_sum != null ? `${fmtInt(it.cost_sum)} ₽` : "—"}</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
      {result.truncated && (
        <div className="small text-body-secondary mt-1">
          Показаны первые {result.shown_items} из {result.total_items} — уточните товар, чтобы сузить список.
        </div>
      )}
    </>
  );
}

export function SalesForecastView({ result }) {
  const days = (result.days || []).filter((d) => d.forecast_revenue != null);
  if (days.length === 0) {
    return <div className="text-body-secondary small">Недостаточно данных для прогноза.</div>;
  }
  const max = Math.max(...days.map((d) => d.range_high ?? d.forecast_revenue));
  return (
    <>
      <div className="small text-body-secondary mb-2">
        Прогноз для г. {result.city} · на основе {result.history_days_used} дней истории ({result.history_date_from} —{" "}
        {result.history_date_to})
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        {days.map((d, i) => (
          <div key={i}>
            <div className="d-flex justify-content-between small">
              <span>
                {d.date} ({d.dow_label})
                {d.calendar_type && d.calendar_type !== "обычный день" && (
                  <CBadge color="info" className="ms-2">
                    {d.calendar_type}
                  </CBadge>
                )}
              </span>
              <span className="text-body-secondary">
                {fmtInt(d.range_low)}–{fmtInt(d.range_high)} ₽
                {d.forecast_guests != null && ` · ~${fmtInt(d.forecast_guests)} гостей`}
              </span>
            </div>
            <CProgress thin color="info">
              <CProgressBar value={(d.forecast_revenue / max) * 100} />
            </CProgress>
          </div>
        ))}
      </div>
      <div className="small text-body-secondary fst-italic">{result.method_note}</div>
    </>
  );
}

const MATRIX_META = {
  stars: { label: "Звёзды", hint: "популярно и маржинально — продвигать", color: "success" },
  plowhorses: { label: "Ломовые лошади", hint: "популярно, низкая маржа — поднять цену", color: "warning" },
  puzzles: { label: "Загадки", hint: "маржинально, но не продаётся — продвигать", color: "info" },
  dogs: { label: "Собаки", hint: "непопулярно и невыгодно — кандидат на вывод", color: "danger" },
};

export function MenuMatrixView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="row g-2">
        {Object.entries(MATRIX_META).map(([key, meta]) => {
          const items = result[key] || [];
          return (
            <div className="col-md-6" key={key}>
              <div className="border rounded p-2 h-100">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <CBadge color={meta.color}>{meta.label}</CBadge>
                  <span className="small text-body-secondary">{result.counts?.[key] ?? items.length}</span>
                </div>
                <div className="small text-body-secondary mb-1 fst-italic">{meta.hint}</div>
                {items.length === 0 ? (
                  <div className="small text-body-secondary">—</div>
                ) : (
                  <ul className="mb-0 ps-3 small">
                    {items.slice(0, 5).map((it, i) => (
                      <li key={i}>
                        {it.name} — {fmtInt(it.qty)} шт, {it.margin_percent}% маржа
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function DaypartView({ result }) {
  if (result.note || !result.hours?.length) {
    return <div className="text-body-secondary small">{result.note || "Нет данных."}</div>;
  }
  const hours = result.hours;
  return (
    <>
      <PeriodCaption result={result} />
      <div style={{ maxHeight: 220 }}>
        <CChartBar
          data={{
            labels: hours.map((h) => `${h.hour}:00`),
            datasets: [
              {
                label: "Ср. выручка, ₽",
                backgroundColor: hours.map((h) => (h.hour === result.peak_hour ? "#41B883" : "#8a7a68")),
                data: hours.map((h) => h.avg_revenue),
              },
            ],
          }}
          options={{ plugins: { legend: { display: false } }, maintainAspectRatio: false }}
        />
      </div>
      <div className="small text-body-secondary mt-2">
        Пик: {result.peak_hour}:00 · Затишье: {result.quiet_hour}:00
      </div>
    </>
  );
}

export function DiscountEffectivenessView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-wrap gap-2 mb-2">
        <StatTile label="Валовая выручка" value={`${fmtInt(result.total_gross)} ₽`} />
        <StatTile label="Сумма скидок" value={`${fmtInt(result.total_discount)} ₽`} />
        <StatTile label="Доля скидок" value={`${result.overall_discount_percent}%`} />
      </div>
      <div className="small fw-semibold text-body-secondary mb-1">Больше всего скидок получили</div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Блюдо</CTableHeaderCell>
            <CTableHeaderCell>Скидка</CTableHeaderCell>
            <CTableHeaderCell>% от цены</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {(result.top_discounted_dishes || []).map((d, i) => (
            <CTableRow key={i}>
              <CTableDataCell>{d.name}</CTableDataCell>
              <CTableDataCell>{fmtInt(d.discount_sum)} ₽</CTableDataCell>
              <CTableDataCell>{d.discount_percent}%</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </>
  );
}

export function PromotionsReportView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const promotions = result.promotions || [];
  return (
    <>
      <PeriodCaption result={result} />
      <div className="d-flex flex-wrap gap-2 mb-2">
        <StatTile label="Сумма скидок всего" value={`${fmtInt(result.total_discount_sum)} ₽`} />
        <StatTile label="Акций/скидок" value={result.promotions_count} />
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Акция / скидка</CTableHeaderCell>
            <CTableHeaderCell>Применений</CTableHeaderCell>
            <CTableHeaderCell>Заказов</CTableHeaderCell>
            <CTableHeaderCell>Сумма скидки</CTableHeaderCell>
            <CTableHeaderCell>Ср. % скидки</CTableHeaderCell>
            <CTableHeaderCell>Доля в скидках</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {promotions.map((p, i) => (
            <CTableRow key={i}>
              <CTableDataCell>{p.name}</CTableDataCell>
              <CTableDataCell>{fmtInt(p.applications)}</CTableDataCell>
              <CTableDataCell>{fmtInt(p.orders)}</CTableDataCell>
              <CTableDataCell>{fmtInt(p.discount_sum)} ₽</CTableDataCell>
              <CTableDataCell>{p.avg_discount_percent}%</CTableDataCell>
              <CTableDataCell>{p.share_of_total_discount_percent}%</CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </>
  );
}

export function StockDepletionView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const items = result.items || [];
  if (items.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  return (
    <>
      <div className="small text-body-secondary mb-2">
        Расход оценён за последние {result.based_on_days} дней · отслеживается позиций: {result.tracked_ingredients}
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Товар</CTableHeaderCell>
            <CTableHeaderCell>Склад</CTableHeaderCell>
            <CTableHeaderCell>Остаток</CTableHeaderCell>
            <CTableHeaderCell>Расход/день</CTableHeaderCell>
            <CTableHeaderCell>Хватит на</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {items.map((it, i) => (
            <CTableRow key={i}>
              <CTableDataCell>{it.product}</CTableDataCell>
              <CTableDataCell className="text-body-secondary">{it.store || "—"}</CTableDataCell>
              <CTableDataCell>
                {it.current_balance} {it.unit}
              </CTableDataCell>
              <CTableDataCell>
                {it.daily_consumption} {it.unit}/д
              </CTableDataCell>
              <CTableDataCell>
                <CBadge color={it.days_remaining < 3 ? "danger" : it.days_remaining < 7 ? "warning" : "secondary"}>
                  {it.days_remaining} дн.
                </CBadge>
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
    </>
  );
}

const ANOMALY_META = {
  negative_balance: { label: "Отрицательный остаток", color: "danger" },
  dead_stock: { label: "Не расходуется 2 недели", color: "secondary" },
  consumption_spike: { label: "Скачок расхода", color: "warning" },
  consumption_drop: { label: "Падение расхода", color: "info" },
};

export function StockAnomaliesView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const anomalies = result.anomalies || [];
  if (anomalies.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  return (
    <>
      <div className="small text-body-secondary mb-2">
        За последнюю неделю против предыдущей · окно {result.window_days} дн. · найдено: {result.total_anomalies}
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Тип</CTableHeaderCell>
            <CTableHeaderCell>Товар</CTableHeaderCell>
            <CTableHeaderCell>Склад</CTableHeaderCell>
            <CTableHeaderCell>Остаток</CTableHeaderCell>
            <CTableHeaderCell>Расход/день (неделя/прошлая)</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {anomalies.map((a, i) => {
            const meta = ANOMALY_META[a.type] || { label: a.type, color: "secondary" };
            return (
              <CTableRow key={i}>
                <CTableDataCell>
                  <CBadge color={meta.color}>{meta.label}</CBadge>
                </CTableDataCell>
                <CTableDataCell>{a.product}</CTableDataCell>
                <CTableDataCell className="text-body-secondary">{a.store || "—"}</CTableDataCell>
                <CTableDataCell className={a.current_balance < 0 ? "text-danger fw-semibold" : undefined}>
                  {a.current_balance} {a.unit}
                </CTableDataCell>
                <CTableDataCell className="text-body-secondary">
                  {a.daily_consumption_last_week != null
                    ? `${a.daily_consumption_last_week} / ${a.daily_consumption_prior_week}`
                    : "—"}
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>
      {result.truncated && (
        <div className="small text-body-secondary mt-1">Показаны не все аномалии — список длиннее.</div>
      )}
    </>
  );
}

const URGENCY_META = {
  critical: { label: "Критично", color: "danger" },
  warning: { label: "Предупреждение", color: "warning" },
  watch: { label: "Наблюдение", color: "secondary" },
};

export function PurchaseRiskListView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const items = result.items || [];
  if (items.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  return (
    <>
      <div className="small text-body-secondary mb-2">
        Расход оценён за последние {result.based_on_days} дней · заказ рассчитан с запасом на{" "}
        {result.reorder_buffer_days} дней
      </div>
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Приоритет</CTableHeaderCell>
            <CTableHeaderCell>Товар</CTableHeaderCell>
            <CTableHeaderCell>Склад</CTableHeaderCell>
            <CTableHeaderCell>Остаток</CTableHeaderCell>
            <CTableHeaderCell>Хватит на</CTableHeaderCell>
            <CTableHeaderCell>Заказать</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {items.map((it, i) => {
            const meta = URGENCY_META[it.urgency] || { label: it.urgency, color: "secondary" };
            return (
              <CTableRow key={i}>
                <CTableDataCell>
                  <CBadge color={meta.color}>{meta.label}</CBadge>
                </CTableDataCell>
                <CTableDataCell>{it.product}</CTableDataCell>
                <CTableDataCell className="text-body-secondary">{it.store || "—"}</CTableDataCell>
                <CTableDataCell>
                  {it.current_balance} {it.unit}
                </CTableDataCell>
                <CTableDataCell>{it.days_remaining} дн.</CTableDataCell>
                <CTableDataCell className="fw-semibold">
                  {it.suggested_order_qty} {it.unit}
                </CTableDataCell>
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>
      {result.truncated && (
        <div className="small text-body-secondary mt-1">Показаны не все позиции — список длиннее.</div>
      )}
    </>
  );
}

export function InventoryAuditView({ result }) {
  if (result.note) return <div className="text-body-secondary small">{result.note}</div>;
  const items = result.items || [];
  if (items.length === 0) return <div className="text-body-secondary small">Нет данных.</div>;
  return (
    <>
      <PeriodCaption result={result} />
      <div className="small text-body-secondary mb-2">
        Инвентаризации в периоде: {(result.inventory_dates || []).join(", ") || "—"}
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <StatTile label="Излишки" value={`${fmtInt(result.total_surplus_sum)} ₽`} />
        <StatTile label="Недостачи" value={`${fmtInt(result.total_shortage_sum)} ₽`} />
      </div>
      {result.documents?.length > 0 && (
        <div className="mb-3">
          <div className="small fw-semibold text-body-secondary mb-1">Акты</div>
          <CTable small hover responsive className="mb-0">
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>№</CTableHeaderCell>
                <CTableHeaderCell>Дата</CTableHeaderCell>
                <CTableHeaderCell>Склад</CTableHeaderCell>
                <CTableHeaderCell>Создал</CTableHeaderCell>
                <CTableHeaderCell>Последний раз менял</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {result.documents.map((d, i) => (
                <CTableRow key={i}>
                  <CTableDataCell>{d.number || "—"}</CTableDataCell>
                  <CTableDataCell className="text-body-secondary">{d.date}</CTableDataCell>
                  <CTableDataCell className="text-body-secondary">{d.store}</CTableDataCell>
                  <CTableDataCell>{d.created_by || "—"}</CTableDataCell>
                  <CTableDataCell>{d.modified_by || "—"}</CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      )}
      <CTable small hover responsive className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell>Дата</CTableHeaderCell>
            <CTableHeaderCell>Склад</CTableHeaderCell>
            <CTableHeaderCell>Товар</CTableHeaderCell>
            <CTableHeaderCell>Тип</CTableHeaderCell>
            <CTableHeaderCell>Кол-во</CTableHeaderCell>
            <CTableHeaderCell>Сумма</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {items.map((it, i) => (
            <CTableRow key={i}>
              <CTableDataCell className="text-body-secondary">{it.date}</CTableDataCell>
              <CTableDataCell className="text-body-secondary">{it.store}</CTableDataCell>
              <CTableDataCell>{it.product}</CTableDataCell>
              <CTableDataCell>
                <CBadge color={it.type === "surplus" ? "success" : "danger"}>
                  {it.type === "surplus" ? "Излишек" : "Недостача"}
                </CBadge>
              </CTableDataCell>
              <CTableDataCell>{it.qty_diff}</CTableDataCell>
              <CTableDataCell className={it.sum_diff < 0 ? "text-danger" : "text-success"}>
                {fmtInt(it.sum_diff)} ₽
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>
      {result.truncated && (
        <div className="small text-body-secondary mt-1">Показаны не все строки — список длиннее.</div>
      )}
    </>
  );
}

const RENDERERS = {
  get_revenue_overview: (result) => <RevenueOverviewView result={result} />,
  get_category_sales: (result) => <ShareListView result={result} items={result.categories} />,
  get_payment_types: (result) => <ShareListView result={result} items={result.payment_types} />,
  get_waiter_stats: (result) => <ShareListView result={result} items={result.waiters} />,
  get_dish_stats: (result) => <DishStatsView result={result} />,
  get_writeoffs: (result) => <WriteoffsView result={result} />,
  get_dangerous_events: (result) => <DangerousEventsView result={result} />,
  get_cash_shifts: (result) => <CashShiftsView result={result} />,
  get_revenue_trend: (result) => <RevenueTrendView result={result} />,
  get_stock_balances: (result) => <StockBalancesView result={result} />,
  get_sales_forecast: (result) => <SalesForecastView result={result} />,
  get_menu_matrix: (result) => <MenuMatrixView result={result} />,
  get_daypart_analysis: (result) => <DaypartView result={result} />,
  get_discount_effectiveness: (result) => <DiscountEffectivenessView result={result} />,
  get_promotions_report: (result) => <PromotionsReportView result={result} />,
  get_stock_depletion_forecast: (result) => <StockDepletionView result={result} />,
  get_stock_anomalies: (result) => <StockAnomaliesView result={result} />,
  get_purchase_risk_list: (result) => <PurchaseRiskListView result={result} />,
  get_inventory_audit: (result) => <InventoryAuditView result={result} />,
};

/** Renders a single stored tool result by name — used by pinned dashboard cards, outside the chat flow. */
export function renderToolResult(name, result) {
  const renderer = RENDERERS[name];
  return renderer ? renderer(result) : <div className="small text-body-secondary">Неизвестный тип карточки.</div>;
}

/** One card per successful tool call in a turn; failed calls are skipped (the model's text already explains them). */
export default function ToolResultsPanel({ toolResults, serverId }) {
  const items = (toolResults || []).filter((t) => t.result && t.result.ok !== false && RENDERERS[t.name]);
  if (items.length === 0) return null;
  return (
    <div className="d-flex flex-column gap-2 mb-3" style={{ maxWidth: "90%" }}>
      {items.map((t, i) => (
        <CCard key={i} className="border-0 bg-body-tertiary">
          <CCardBody className="py-2 px-3">
            {RENDERERS[t.name](t.result)}
            {serverId && (
              <div className="d-flex justify-content-end mt-2">
                <PinButton
                  serverId={serverId}
                  toolName={t.name}
                  title={TOOL_TITLES[t.name] || t.name}
                  payload={t.result}
                />
              </div>
            )}
          </CCardBody>
        </CCard>
      ))}
    </div>
  );
}
